import React, { useEffect, useMemo, useState } from "react"
import { useAuth0 } from "@auth0/auth0-react"
import { CalendarPlus, Church, HeartHandshake, Music, Soup, UsersRound } from "lucide-react"

const scaleOptions = [
  {
    value: "One neighboring church",
    title: "One neighboring church",
    text: "Start small when the room, kitchen, or volunteer team is small."
  },
  {
    value: "A few nearby churches",
    title: "A few nearby churches",
    text: "Invite a handful of congregations who can actually sit, eat, and talk."
  },
  {
    value: "Open local gathering",
    title: "Open local gathering",
    text: "Open the table wider when the church has room to receive more people."
  },
  {
    value: "Choose churches manually",
    title: "Choose churches manually",
    text: "Pick the specific churches God has put on your heart."
  }
]

const defaultForm = {
  title: "Fellowship Meet",
  date: "",
  startTime: "17:00",
  endTime: "20:00",
  fellowshipScale: "A few nearby churches",
  capacity: "",
  recurringPattern: "Monthly",
  description: "Neighboring churches should not be strangers. Break bread. Share testimony. Pray together. See what God is doing nearby.",
  potluckNotes: "Community potluck. Bring a main dish, side, dessert, or beverage if you can. There will always be a place at the table.",
  worshipNotes: "Musicians and worshippers are welcome for informal worship.",
  testimonyNotes: "Make room for people to share what Christ is doing in their lives and churches.",
  prayerNotes: "Pray together across congregations and denominations.",
  parkingInfo: "",
  kitchenInfo: "",
  accessibilityInfo: "",
  childrenAndFamiliesInfo: "",
  helpNeeded: "",
  hostNotes: "This is not a conference. It is not a pitch. It is the Body of Christ becoming family."
}

const isChurchGroup = (group) => {
  const groupType = (group?.groupType || group?.GroupType || "").toLowerCase()
  return groupType === "congregation" || groupType === "church"
}

const fieldValue = (group, camelName, pascalName) => group?.[camelName] || group?.[pascalName]

const formatChurchLocation = (church) => [church.city, church.state, church.country].filter(Boolean).join(", ")

export default function FellowshipMeetPlanner({ group, onCreated }) {
  const { getAccessTokenSilently } = useAuth0()
  const apiUrl = process.env.REACT_APP_API_URL
  const groupId = fieldValue(group, "id", "Id")
  const [form, setForm] = useState(defaultForm)
  const [suggestions, setSuggestions] = useState([])
  const [selectedChurches, setSelectedChurches] = useState([])
  const [loadingSuggestions, setLoadingSuggestions] = useState(false)
  const [suggestionError, setSuggestionError] = useState("")
  const [notice, setNotice] = useState("")
  const [createdId, setCreatedId] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!groupId || !isChurchGroup(group)) return
    let isMounted = true
    const loadSuggestions = async () => {
      setLoadingSuggestions(true)
      setSuggestionError("")
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_API_AUDIENCE,
          },
        })
        const response = await fetch(`${apiUrl}/api/calendarevents/fellowship-meets/nearby-churches?groupId=${groupId}&limit=20`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        if (!response.ok) {
          throw new Error("Nearby churches could not be loaded.")
        }
        const data = await response.json()
        if (isMounted) {
          setSuggestions(Array.isArray(data) ? data : [])
        }
      } catch (error) {
        if (isMounted) {
          setSuggestionError(error.message || "Nearby churches could not be loaded.")
        }
      } finally {
        if (isMounted) {
          setLoadingSuggestions(false)
        }
      }
    }
    loadSuggestions()
    return () => {
      isMounted = false
    }
  }, [apiUrl, getAccessTokenSilently, group, groupId])

  const selectedCount = selectedChurches.length
  const selectedSuggestionNames = useMemo(() => {
    const selected = new Set(selectedChurches)
    return suggestions
      .filter((church) => selected.has(church.groupId))
      .map((church) => church.congregationName || church.groupName)
  }, [selectedChurches, suggestions])

  if (!isChurchGroup(group)) {
    return null
  }

  const updateField = (name, value) => {
    setForm((current) => ({ ...current, [name]: value }))
  }

  const toggleChurch = (churchId) => {
    setSelectedChurches((current) => (
      current.includes(churchId)
        ? current.filter((item) => item !== churchId)
        : [...current, churchId]
    ))
  }

  const buildDate = (date, time) => {
    if (!date || !time) return null
    return new Date(`${date}T${time}:00`)
  }

  const submitMeet = async (event) => {
    event.preventDefault()
    setNotice("")
    const startDateTime = buildDate(form.date, form.startTime)
    const endDateTime = buildDate(form.date, form.endTime)
    if (!startDateTime) {
      setNotice("Choose a date and start time first.")
      return
    }

    setSubmitting(true)
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
        },
      })
      const response = await fetch(`${apiUrl}/api/calendarevents/fellowship-meets`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          groupId,
          title: form.title,
          description: form.description,
          startDateTime: startDateTime.toISOString(),
          endDateTime: (endDateTime || new Date(startDateTime.getTime() + 3 * 60 * 60 * 1000)).toISOString(),
          fellowshipScale: form.fellowshipScale,
          capacity: form.capacity ? Number(form.capacity) : null,
          inviteGroupIds: selectedChurches,
          potluckNotes: form.potluckNotes,
          worshipNotes: form.worshipNotes,
          testimonyNotes: form.testimonyNotes,
          prayerNotes: form.prayerNotes,
          parkingInfo: form.parkingInfo,
          kitchenInfo: form.kitchenInfo,
          accessibilityInfo: form.accessibilityInfo,
          childrenAndFamiliesInfo: form.childrenAndFamiliesInfo,
          helpNeeded: form.helpNeeded,
          hostNotes: form.hostNotes,
          recurringPattern: form.recurringPattern,
        }),
      })
      if (!response.ok) {
        throw new Error("The Fellowship Meet could not be saved.")
      }
      const created = await response.json()
      setCreatedId(created.id)
      setNotice(`Fellowship Meet saved${selectedCount ? ` and ${selectedCount} church invitation${selectedCount === 1 ? "" : "s"} prepared` : ""}.`)
      onCreated?.(created)
    } catch (error) {
      setNotice(error.message || "The Fellowship Meet could not be saved.")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <section className="fellowship-meet-planner">
      <div className="fellowship-meet-hero">
        <span className="group-section-kicker"><HeartHandshake className="h-4 w-4" /> Fellowship Meets</span>
        <h3>Neighboring churches should not be strangers.</h3>
        <p>Break bread. Share testimony. Pray together. See what God is doing nearby.</p>
        <p className="fellowship-meet-vibe">This is not a conference. It is not a pitch. It is the Body of Christ becoming family.</p>
        <p>This gathering will have a public invitation and QR RSVP page showing its details and the host church location. Do not include private information. Actual attendance reports are for the host and fellowship members.</p>
      </div>

      <form className="fellowship-meet-form" onSubmit={submitMeet}>
        <div className="fellowship-meet-form-grid">
          <label>
            Name this gathering
            <input value={form.title} onChange={(event) => updateField("title", event.target.value)} />
          </label>
          <label>
            Date
            <input type="date" value={form.date} onChange={(event) => updateField("date", event.target.value)} />
          </label>
          <label>
            Starts
            <input type="time" value={form.startTime} onChange={(event) => updateField("startTime", event.target.value)} />
          </label>
          <label>
            Ends
            <input type="time" value={form.endTime} onChange={(event) => updateField("endTime", event.target.value)} />
          </label>
          <label>
            How many people can you warmly receive?
            <input type="number" min="1" value={form.capacity} onChange={(event) => updateField("capacity", event.target.value)} placeholder="Optional" />
          </label>
          <label>
            Rhythm
            <select value={form.recurringPattern} onChange={(event) => updateField("recurringPattern", event.target.value)}>
              <option>Monthly</option>
              <option>Last Saturday monthly</option>
              <option>One-time</option>
            </select>
          </label>
        </div>

        <div className="fellowship-scale-grid" aria-label="Fellowship Meet size">
          {scaleOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              className={form.fellowshipScale === option.value ? "is-selected" : ""}
              onClick={() => updateField("fellowshipScale", option.value)}
            >
              <strong>{option.title}</strong>
              <span>{option.text}</span>
            </button>
          ))}
        </div>

        <div className="fellowship-meet-notes-grid">
          <label>
            What should people know?
            <textarea value={form.description} onChange={(event) => updateField("description", event.target.value)} />
          </label>
          <label>
            <Soup className="h-4 w-4" /> Meal and potluck
            <textarea value={form.potluckNotes} onChange={(event) => updateField("potluckNotes", event.target.value)} />
          </label>
          <label>
            <Music className="h-4 w-4" /> Worship and musicians
            <textarea value={form.worshipNotes} onChange={(event) => updateField("worshipNotes", event.target.value)} />
          </label>
          <label>
            <UsersRound className="h-4 w-4" /> Testimony and prayer
            <textarea value={`${form.testimonyNotes}\n\n${form.prayerNotes}`} onChange={(event) => {
              const [testimonyNotes, ...prayerParts] = event.target.value.split("\n\n")
              updateField("testimonyNotes", testimonyNotes)
              updateField("prayerNotes", prayerParts.join("\n\n"))
            }} />
          </label>
          <label>
            Parking
            <input value={form.parkingInfo} onChange={(event) => updateField("parkingInfo", event.target.value)} placeholder="Parking lot, street parking, van dropoff..." />
          </label>
          <label>
            Kitchen and food setup
            <input value={form.kitchenInfo} onChange={(event) => updateField("kitchenInfo", event.target.value)} placeholder="Kitchen access, outlets, tables..." />
          </label>
          <label>
            Accessibility
            <input value={form.accessibilityInfo} onChange={(event) => updateField("accessibilityInfo", event.target.value)} placeholder="Entrances, seating, bathrooms..." />
          </label>
          <label>
            Children and families
            <input value={form.childrenAndFamiliesInfo} onChange={(event) => updateField("childrenAndFamiliesInfo", event.target.value)} placeholder="Family seating, nursery room, kid-friendly notes..." />
          </label>
          <label>
            Help needed
            <input value={form.helpNeeded} onChange={(event) => updateField("helpNeeded", event.target.value)} placeholder="Greeters, setup, cleanup, food tables..." />
          </label>
          <label>
            Host posture
            <input value={form.hostNotes} onChange={(event) => updateField("hostNotes", event.target.value)} />
          </label>
        </div>

        <section className="fellowship-nearby-churches">
          <div>
            <span className="group-section-kicker"><Church className="h-4 w-4" /> Invite nearby churches</span>
            <h4>Start with churches close enough to become friends.</h4>
            <p>Select one church, a few churches, or nobody yet. TYK can still save the meet and you can invite people later.</p>
          </div>
          {loadingSuggestions ? (
            <p className="fellowship-meet-status">Looking for churches nearby...</p>
          ) : suggestionError ? (
            <p className="fellowship-meet-status is-error">{suggestionError}</p>
          ) : suggestions.length === 0 ? (
            <p className="fellowship-meet-status">No nearby church records were found yet.</p>
          ) : (
            <div className="fellowship-church-grid">
              {suggestions.map((church) => (
                <label key={church.groupId} className="fellowship-church-option">
                  <input
                    type="checkbox"
                    checked={selectedChurches.includes(church.groupId)}
                    onChange={() => toggleChurch(church.groupId)}
                  />
                  <span>
                    <strong>{church.congregationName || church.groupName}</strong>
                    <small>{[formatChurchLocation(church), church.denominationOrTradition].filter(Boolean).join(" • ")}</small>
                    <em>{church.matchReason}</em>
                  </span>
                </label>
              ))}
            </div>
          )}
          {selectedSuggestionNames.length > 0 && (
            <p className="fellowship-selected-summary">Selected: {selectedSuggestionNames.join(", ")}</p>
          )}
        </section>

        {notice && <p className="fellowship-meet-notice">{notice}</p>}
        {createdId && <p><a href={`/events/${createdId}`}>Open gathering, share its QR invitation, and connect an ongoing fellowship for ideas and voting.</a></p>}
        <button className="fellowship-meet-submit" type="submit" disabled={submitting}>
          <CalendarPlus className="h-4 w-4" />
          {submitting ? "Saving meet..." : "Create Fellowship Meet"}
        </button>
      </form>
    </section>
  )
}
